import { withDependencies, named } from '@wix/thunderbolt-ioc'
import { name } from './symbols'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	EditorFeatureConfigSymbol,
	ViewerManagerStructureAPISymbol,
} from '@wix/thunderbolt-symbols'
import type { MeasuresApiEditorConfig } from './types'
import { getDisplayedId, getFullId, getItemId, isDisplayedOnly, isRepeater } from '@wix/thunderbolt-commons'
import type { Component, IMeasuresApi, GetMeasureFromRectRelativeToParent } from '@wix/thunderbolt-becky-types'
import type { SiteMeasureMap, ViewerAPI } from 'thunderbolt-viewer-manager-types'
import { ComponentsMeasuresAPISymbol, IComponentsMeasuresAPI } from 'ds-feature-components-measures'
import { getAutoHeightWrapperId } from '@wix/thunderbolt-becky-root'

const isPopupContainer = (compStructure: Component) =>
	compStructure.componentType === 'wysiwyg.viewer.components.PopupContainer'

const roundToNearestHalf = (num: number) => {
	const nearestHalf = Math.round(num * 2) / 2
	return 1 / nearestHalf === -Infinity ? 0 : nearestHalf
}

const getNotRotatedAbsoluteTop = (absoluteTop: number, height: number, offsetHeight: number, siteScale: number) =>
	roundToNearestHalf(absoluteTop + (height / siteScale - offsetHeight) / 2)

const getNotRotatedAbsoluteLeft = (absoluteLeft: number, width: number, offsetWidth: number, siteScale: number) =>
	roundToNearestHalf(absoluteLeft + (width / siteScale - offsetWidth) / 2)

const measuresApiFactory = (
	config: MeasuresApiEditorConfig,
	window: NonNullable<BrowserWindow>,
	structureViewerApi: ViewerAPI['structure'],
	componentsMeasuresAPI: IComponentsMeasuresAPI
): IMeasuresApi => {
	const roundMeasures = (num: number): number => {
		return roundToNearestHalf(num)
	}

	const handleRotation = (
		measureProperty: 'top' | 'left',
		measureValue: number,
		elementRect: DOMRect,
		element: HTMLElement,
		siteScale: number,
		offsetValue?: number
	) =>
		measureProperty === 'top'
			? getNotRotatedAbsoluteTop(
					measureValue,
					roundMeasures(elementRect.height),
					offsetValue || element.offsetHeight,
					siteScale
			  )
			: getNotRotatedAbsoluteLeft(
					measureValue,
					roundMeasures(elementRect.width),
					offsetValue || element.offsetWidth,
					siteScale
			  )

	const isRotated = (compId: string) => config.rotatedComponent[getFullId(compId)]

	const getSiteX = () => {
		const { siteWidth } = config
		const { clientWidth } = window.document.body

		return clientWidth > siteWidth ? 0.5 * (clientWidth - siteWidth) : 0
	}

	const getParentIdFromRootStructure = (compId: string) => {
		const isRepeatedComponent = isDisplayedOnly(compId)
		if (!isRepeatedComponent) {
			return config.compIdToAncestors[compId][0]
		}

		const nonInflatedParent = config.compIdToAncestors[getFullId(compId)][0]
		if (isRepeater(structureViewerApi.getStructure(nonInflatedParent).componentType)) {
			return nonInflatedParent
		}

		const itemId = getItemId(compId)
		return getDisplayedId(nonInflatedParent, itemId)
	}

	const isCompFixed = (element: HTMLElement | null) => {
		let currentElement = element
		while (currentElement) {
			if (getComputedStyle(currentElement).position === 'fixed') {
				return true
			}
			currentElement = currentElement.parentElement
		}

		return false
	}

	const getMeasureFromDOM = (element: HTMLElement, measureType: 'top' | 'left' | 'width') => {
		const { siteScale } = config
		const elementRect = element?.getBoundingClientRect()

		return (elementRect?.[measureType] || 0) / siteScale
	}

	const getParentMeasure = (parentId: string, measureType: 'top' | 'left', measureMap: SiteMeasureMap): number => {
		const parentCustomMeasure = componentsMeasuresAPI.tryUsingCustomMeasure(
			parentId,
			measureType,
			measureMap,
			measureApi
		)
		const parentOffsetMeasure =
			componentsMeasuresAPI.tryUsingCustomMeasure(
				parentId,
				measureType === 'top' ? 'topOffset' : 'leftOffset',
				measureMap,
				measureApi
			) || 0

		if (componentsMeasuresAPI.isValidMeasure(parentCustomMeasure)) {
			const parentOfParentId = structureViewerApi.getStructure(parentId).parent!
			return parentCustomMeasure + getParentMeasure(parentOfParentId, measureType, measureMap)
		}
		const parentElement = getCompElement(parentId)!
		return getMeasureFromDOM(parentElement, measureType) + parentOffsetMeasure
	}

	const getMeasureFromRectRelativeToParent = ({
		element,
		compId,
		measure,
		offsetValue,
		forceIsRotated,
		measureMap,
		useCustomMeasureForParent = false,
	}: GetMeasureFromRectRelativeToParent) => {
		const parentId = getParentIdFromRootStructure(compId)
		if (parentId === 'main_MF') {
			return 0
		}
		const { siteScale } = config
		const parentElement = getCompElement(parentId)
		let parentMeasure =
			useCustomMeasureForParent || !parentElement
				? getParentMeasure(parentId, measure, measureMap)
				: getMeasureFromDOM(parentElement, measure)

		let elementMeasure = getMeasureFromDOM(element, measure)

		const scroll = window[measure === 'top' ? 'scrollY' : 'scrollX']
		if (scroll !== 0) {
			const isParentFixed = isCompFixed(parentElement)

			parentMeasure += isParentFixed ? 0 : scroll
			elementMeasure += isParentFixed || isCompFixed(element) ? 0 : scroll
		}

		const isCompRotated = forceIsRotated || isRotated(compId)
		if (isCompRotated) {
			const elementRect = element.getBoundingClientRect()
			elementMeasure = handleRotation(measure, elementMeasure, elementRect, element, siteScale, offsetValue)
		}

		const isParentRotated = isRotated(parentId)
		const parentRect = parentElement?.getBoundingClientRect()
		if (parentRect && isParentRotated) {
			parentMeasure = handleRotation(measure, parentMeasure, parentRect, parentElement!, siteScale)
		}

		return roundMeasures(elementMeasure - parentMeasure)
	}

	const getRotatedWrapper = (compId: string) =>
		window.document.querySelector(`[data-mesh-id="${compId}-rotated-wrapper"]`)

	const isAutoHeightComp = (compId: string) => !!config.autoHeightCompIds[getFullId(compId)]

	const getCompIdSelectorForMesh = (compId: string) =>
		isAutoHeightComp(compId) ? getAutoHeightWrapperId(compId) : compId

	const getCompElement = (compId: string) => window.document.getElementById(getCompIdSelectorForMesh(compId))

	const measureApi = {
		isCompFixed,
		getMeasureFromRectRelativeToParent,

		getCompLeft: (
			element: HTMLElement,
			compId: string,
			measureMap: SiteMeasureMap,
			useCustomMeasureForParent?: boolean
		) => {
			// TODO: https://jira.wixpress.com/browse/TB-3720
			const compStructure = structureViewerApi.getStructure(compId)
			if (!compStructure) {
				return 0
			}

			let left = getMeasureFromRectRelativeToParent({
				element,
				compId,
				measure: 'left',
				measureMap,
				useCustomMeasureForParent,
			})

			if (compStructure.layout.fixedPosition) {
				return left
			} else {
				const structureX = compStructure.layout.x || 0

				let rotatedWrapper = null
				if (isRotated(compId)) {
					rotatedWrapper = getRotatedWrapper(compId)
				}
				// PopupContainer renders without 'margin-left' and uses grid instead.
				// We need to decrease the siteX (which equals to the computed margin-left for other components)
				// in order to get the left relative to page.
				if (isPopupContainer(compStructure)) {
					const siteX = getSiteX()
					left -= siteX
				} else {
					const parentContentArea = config.parentContentArea[compId]
					if (parentContentArea) {
						const parent = (rotatedWrapper || element).parentElement
						// @ts-ignore, parent is always exists if declared a contentArea for component
						const parentWidth = roundMeasures(getMeasureFromDOM(parent, 'width'))
						const leftOffset = (parentWidth - parentContentArea.width) * parentContentArea.alignment || 0
						left -= leftOffset
					}
				}

				const diff = Math.abs(structureX - left)
				return diff <= 0.5 ? structureX : left
			}
		},
		getCompTop: (
			element: HTMLElement,
			compId: string,
			measureMap: SiteMeasureMap,
			useCustomMeasureForParent?: boolean
		) =>
			getMeasureFromRectRelativeToParent({
				element,
				compId,
				measure: 'top',
				measureMap,
				useCustomMeasureForParent,
			}),
		getCompWidth: (element: HTMLElement, compId: string) => {
			if (isRotated(compId)) {
				return element.offsetWidth
			}

			const { siteScale } = config
			const boundingClientRect = element.getBoundingClientRect()
			return roundMeasures(boundingClientRect.width / siteScale)
		},
		getCompHeight: (element: HTMLElement, compId: string) => {
			if (isRotated(compId)) {
				return element.offsetHeight
			}

			const compStructure = structureViewerApi.getStructure(compId)
			const heightFromStructure = compStructure?.layout.height || 0
			const { siteScale } = config
			const heightFromDOM = (element.getBoundingClientRect().height || 0) / siteScale

			return roundMeasures(heightFromDOM || heightFromStructure)
		},
		getCompElement,
		getCompIdSelectorForMesh,
		isAutoHeightComp,
	}
	return measureApi
}

export const MeasuresApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		ViewerManagerStructureAPISymbol,
		ComponentsMeasuresAPISymbol,
	],
	measuresApiFactory
)
